import { createAsyncThunk, unwrapResult } from "@reduxjs/toolkit";
import { BaseSessionMetadata, ChatMessage, Session } from "core";
import { RemoteSessionMetadata } from "core/control-plane/client";
import { NEW_SESSION_TITLE } from "core/util/constants";
import { renderChatMessage } from "core/util/messageContent";
import { IIdeMessenger } from "../../context/IdeMessenger";
import { selectSelectedChatModel } from "../slices/configSlice";
import {
    deleteSessionMetadata,
    newSession,
    setAllSessionMetadata,
    setIsSessionMetadataLoading,
    updateSessionMetadata,
} from "../slices/sessionSlice";
import { ThunkApiType } from "../store";

const MAX_TITLE_LENGTH = 100;

// Async session functions live in thunks (because of IDE messaging mostly)
// see sessionSlice for sync redux session functions

export async function getSession(
  ideMessenger: IIdeMessenger,
  id: string,
): Promise<Session> {
  const result = await ideMessenger.request("history/load", { id });
  if (result.status === "error") {
    throw new Error(result.error);
  }
  return result.content;
}

export async function getRemoteSession(
  ideMessenger: IIdeMessenger,
  remoteId: string,
): Promise<Session> {
  const result = await ideMessenger.request("history/loadRemote", { remoteId });
  if (result.status === "error") {
    throw new Error(result.error);
  }
  return result.content;
}

// Track if we're already loading to prevent concurrent calls
let isLoadingMetadata = false;
let lastMetadataResult: (BaseSessionMetadata | RemoteSessionMetadata)[] | null = null;

export const refreshSessionMetadata = createAsyncThunk<
  RemoteSessionMetadata[] | BaseSessionMetadata[],
  {
    offset?: number;
    limit?: number;
  },
  ThunkApiType
>("session/refreshMetadata", async ({ offset, limit }, { dispatch, extra, getState }) => {
  // Prevent concurrent calls
  if (isLoadingMetadata) {
    return lastMetadataResult || [];
  }

  try {
    isLoadingMetadata = true;
    const result = await extra.ideMessenger.request("history/list", {
      limit,
      offset,
    });
    
    if (result.status === "error") {
      // Even on error, stop loading
      isLoadingMetadata = false;
      dispatch(setIsSessionMetadataLoading(false));
      // Only update if current state is different
      const currentState = getState().session.allSessionMetadata;
      if (currentState.length > 0) {
        dispatch(setAllSessionMetadata([]));
      }
      lastMetadataResult = [];
      return [];
    }

    const newMetadata = result.content || [];
    const currentState = getState().session.allSessionMetadata;
    
    // Compare if data actually changed to prevent unnecessary re-renders
    const hasChanged = 
      newMetadata.length !== currentState.length ||
      newMetadata.some((item, index) => {
        const currentItem = currentState[index];
        return !currentItem || item.sessionId !== currentItem.sessionId;
      });

    isLoadingMetadata = false;
    dispatch(setIsSessionMetadataLoading(false));
    
    // Only update state if data actually changed
    if (hasChanged) {
      dispatch(setAllSessionMetadata(newMetadata));
    }
    
    lastMetadataResult = newMetadata;
    return newMetadata;
  } catch (error) {
    // Always stop loading, even on error
    isLoadingMetadata = false;
    dispatch(setIsSessionMetadataLoading(false));
    
    // Only update if current state is different
    const currentState = getState().session.allSessionMetadata;
    if (currentState.length > 0) {
      dispatch(setAllSessionMetadata([]));
    }
    lastMetadataResult = [];
    return [];
  }
});

export const deleteSession = createAsyncThunk<void, string, ThunkApiType>(
  "session/delete",
  async (id, { getState, dispatch, extra }) => {
    dispatch(deleteSessionMetadata(id)); // optimistic
    const state = getState();
    if (id === state.session.id) {
      await dispatch(loadLastSession());
    }
    const result = await extra.ideMessenger.request("history/delete", { id });
    if (result.status === "error") {
      throw new Error(result.error);
    }
    void dispatch(refreshSessionMetadata({}));
  },
);

export const updateSession = createAsyncThunk<void, Session, ThunkApiType>(
  "session/update",
  async (session, { extra, dispatch }) => {
    dispatch(
      updateSessionMetadata({
        sessionId: session.sessionId,
        title: session.title,
      }),
    ); // optimistic session metadata update
    await extra.ideMessenger.request("history/save", session);
    await dispatch(refreshSessionMetadata({}));
  },
);

/*
 this is only used for the custom focusContinueSessionId command at the moment
*/
export const loadSession = createAsyncThunk<
  void,
  {
    sessionId: string;
    saveCurrentSession: boolean;
  },
  ThunkApiType
>(
  "session/load",
  async ({ sessionId, saveCurrentSession: save }, { extra, dispatch }) => {
    if (save) {
      const result = await dispatch(
        saveCurrentSession({
          openNewSession: false,
          generateTitle: true,
        }),
      );
      unwrapResult(result);
    }
    const session = await getSession(extra.ideMessenger, sessionId);
    dispatch(newSession(session));
  },
);

export const loadRemoteSession = createAsyncThunk<
  void,
  {
    remoteId: string;
    saveCurrentSession: boolean;
  },
  ThunkApiType
>(
  "session/loadRemote",
  async ({ remoteId, saveCurrentSession: save }, { extra, dispatch }) => {
    if (save) {
      const result = await dispatch(
        saveCurrentSession({
          openNewSession: false,
          generateTitle: true,
        }),
      );
      unwrapResult(result);
    }
    const session = await getRemoteSession(extra.ideMessenger, remoteId);
    dispatch(newSession(session));
  },
);

export const loadLastSession = createAsyncThunk<void, void, ThunkApiType>(
  "session/loadLast",
  async (_, { extra, dispatch, getState }) => {
    let lastSessionId = getState().session.lastSessionId;

    // const lastSessionResult = await extra.ideMessenger.request("history/list", {
    //   limit: 1,
    // });
    // if (lastSessionResult.status === "success") {
    //   lastSessionId = lastSessionResult.content.at(0)?.sessionId;
    // }

    if (!lastSessionId) {
      dispatch(newSession());
      return;
    }

    let session: Session;
    try {
      session = await getSession(extra.ideMessenger, lastSessionId);
    } catch {
      // retry again after 1 sec
      await new Promise((resolve) => setTimeout(resolve, 1000));
      session = await getSession(extra.ideMessenger, lastSessionId);
    }
    dispatch(newSession(session));
  },
);

function getChatTitleFromMessage(message: ChatMessage) {
  const text =
    renderChatMessage(message)
      .split("\n")
      .filter((l) => l.trim() !== "")
      .slice(-1)[0] || "";

  // Truncate
  if (text.length > MAX_TITLE_LENGTH) {
    return text.slice(0, MAX_TITLE_LENGTH - 3) + "...";
  }
  return text;
}

export const saveCurrentSession = createAsyncThunk<
  void,
  { openNewSession: boolean; generateTitle: boolean },
  ThunkApiType
>(
  "session/saveCurrent",
  async ({ openNewSession, generateTitle }, { dispatch, extra, getState }) => {
    const state = getState();
    if (state.session.history.length === 0) {
      return;
    }

    if (openNewSession) {
      dispatch(newSession());
    }

    // New session has already been dispatched
    // Now save previous session and update chat title if relevant
    let title = state.session.title;
    if (title === NEW_SESSION_TITLE) {
      const selectedChatModel = selectSelectedChatModel(state);

      if (!state.config.config?.disableSessionTitles && selectedChatModel) {
        let assistantResponse = state.session.history
          ?.filter((h) => h.message.role === "assistant")[0]
          ?.message?.content?.toString();

        if (assistantResponse && generateTitle) {
          try {
            const result = await extra.ideMessenger.request(
              "chatDescriber/describe",
              {
                text: assistantResponse,
              },
            );
            if (result.status === "success" && result.content) {
              title = result.content;
            }
          } catch (e) {
            console.error("Error generating chat title", e);
          }
        }
      }
      // Fallbacks if above doesn't work out or session titles disabled
      if (title === NEW_SESSION_TITLE) {
        title = getChatTitleFromMessage(state.session.history[0].message);
      }
    }
    // More fallbacks in case of no title
    if (!title.length) {
      const metadata = getState().session.allSessionMetadata.find(
        (m) => m.sessionId === state.session.id,
      );
      if (metadata?.title) {
        title = metadata.title;
      }
    }
    if (!title.length) {
      title = NEW_SESSION_TITLE;
    }

    const session: Session = {
      sessionId: state.session.id,
      title,
      workspaceDirectory: window.workspacePaths?.[0] || "",
      history: state.session.history,
    };

    const result = await dispatch(updateSession(session));
    unwrapResult(result);
  },
);
